import { type FC } from "react";

import { Check } from "lucide-react";

import { cn } from "@/shared/utils/core";

import accountSetupImg from "@/shared/assets/illustrations/triage-account-setup.svg";
import confirmAuthorityImg from "@/shared/assets/illustrations/triage-confirm-authority.svg";
import moveForwardImg from "@/shared/assets/illustrations/triage-move-forward.svg";

interface Step {
  status: "completed" | "active" | "upcoming";
  title: string;
  badge?: string;
  description: string;
  illustration: string;
  illustrationAlt: string;
}

const getSteps = (personFirstName: string): Step[] => [
  {
    status: "completed",
    title: personFirstName
      ? `${personFirstName}'s estate now has a home`
      : "The estate now has a home",
    description: "Your account is set up, and Elayne is already organizing the details you shared.",
    illustration: accountSetupImg,
    illustrationAlt: "Target with arrows illustration",
  },
  {
    status: "active",
    title: "Confirm your authority",
    badge: "About 3 minutes",
    description: `Share ${personFirstName || "the person who passed"}'s death certificate, and let us know if you have legal authority to manage the estate. Not sure? Share whatever you have, and we'll walk through the rest step by step.`,
    illustration: confirmAuthorityImg,
    illustrationAlt: "Person working at a laptop illustration",
  },
  {
    status: "upcoming",
    title: "Move the estate forward",
    description:
      "Once you have legal authority, Elayne Asset Search and identity protection open up. Need legal authority first? Estate Setup walks you through getting it, with an Estate Expert call and attorney referrals available.",
    illustration: moveForwardImg,
    illustrationAlt: "Person celebrating achievement illustration",
  },
];

interface StepIconProps {
  status: Step["status"];
}

const StepIcon: FC<StepIconProps> = ({ status }) => {
  if (status === "completed") {
    return (
      <div className="relative z-10 flex shrink-0 items-center justify-center rounded-full bg-brand size-6">
        <Check className="size-3.5 text-white" strokeWidth={3} />
      </div>
    );
  }

  if (status === "active") {
    return (
      <div className="relative z-10 flex shrink-0 items-center justify-center size-6">
        <div className="size-4 rounded-full border-2 border-brand bg-white" />
      </div>
    );
  }

  return (
    <div className="relative z-10 flex shrink-0 items-center justify-center size-6">
      <div className="size-4 rounded-full border-2 border-strong bg-white" />
    </div>
  );
};

interface StepCardProps {
  step: Step;
}

const StepCard: FC<StepCardProps> = ({ step }) => {
  const isCompleted = step.status === "completed";
  const isActive = step.status === "active";

  return (
    <div
      className={cn(
        "flex flex-1 min-w-0 items-center rounded-lg overflow-hidden",
        isActive ? "bg-white border-2 border-brand" : "bg-subtle border border-primary",
      )}
    >
      <div className="flex flex-1 min-w-0 flex-col gap-1 px-4 py-6">
        <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
          <p className={cn("font-semibold sm:whitespace-nowrap", !isCompleted && "text-primary")}>
            {step.title}
          </p>
          {step.badge && (
            <p className="font-medium text-2xs text-brand whitespace-nowrap">{step.badge}</p>
          )}
        </div>
        <p className="text-secondary">{step.description}</p>
      </div>
      <img
        src={step.illustration}
        alt={step.illustrationAlt}
        className="hidden size-[120px] shrink-0 object-contain sm:block"
      />
    </div>
  );
};

interface TriageModalStepperStepProps {
  personFirstName: string;
  userFirstName?: string;
}

export const TriageModalStepperStep: FC<TriageModalStepperStepProps> = ({
  personFirstName,
  userFirstName,
}) => {
  const steps = getSteps(personFirstName);
  const greeting = userFirstName || personFirstName || "there";

  return (
    <div className="flex flex-col pb-2">
      <h2 className="text-center">Welcome {greeting}, you're in the right place</h2>

      <div className="mt-6 flex flex-col">
        {steps.map((step, index) => {
          const isFirst = index === 0;
          const isLast = index === steps.length - 1;
          const prevStep = steps[index - 1];
          return (
            <div key={step.title} className="flex gap-4 sm:gap-6">
              <div className="flex w-6 shrink-0 flex-col items-center">
                {isFirst ? (
                  <div className="flex-1" />
                ) : (
                  <div
                    className={cn(
                      "mb-2 flex-1 border-l-2",
                      prevStep.status === "completed"
                        ? "border-brand"
                        : "border-dashed border-strong",
                    )}
                    aria-hidden="true"
                  />
                )}
                <StepIcon status={step.status} />
                {isLast ? (
                  <div className="flex-1" />
                ) : (
                  <div
                    className={cn(
                      "mt-2 flex-1 border-l-2",
                      step.status === "completed" ? "border-brand" : "border-dashed border-strong",
                    )}
                    aria-hidden="true"
                  />
                )}
              </div>
              <div className="min-w-0 flex-1 py-3">
                <StepCard step={step} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
